import React, { useEffect, useRef, useState } from 'react';
import MenuHeader from './components/MenuHeader';
import { useSyncCopilotLeftMenuCollapse } from '@/pages/aigc/pages/Agent/hooks/useCopilotLeftMenuCollapsed';
import MenuListWrap from './components/MenuListWrap';
import FoldButton from '@/pages/dashboard/components/FoldButton';
import GlobalMask from './components/GlobalMask';
import { ILeftMenuPropsType } from './type';
import { useDispatch, getLocale } from 'umi';
import './index.scss';
import { DEFAULT_WIDTH, MAX_WIDTH } from '@/pages/dashboard/_config/constant';
import { LANGUAGE_EN } from '@/pages/home/_config/config';

/**
 * 左侧菜单收缩/展开 transform动画结束后，再触发resize事件，避免动画未结束时，系统其他地方监听到resize事件后，计算的宽度不正确
 * 已知场景
 * 1、展开/收缩菜单后，echarts溢出 或者 留白过多
 */
function useMenuTransitionEnd() {
  const resizeTimer = useRef<number | null>(null);

  const clearResizeTimer = () => {
    if (resizeTimer.current) {
      window.clearTimeout(resizeTimer.current);
      resizeTimer.current = null;
    }
  };

  const handleMenuWidthTransitionEnd: React.TransitionEventHandler<HTMLDivElement> = () => {
    clearResizeTimer();
    resizeTimer.current = window.setTimeout(() => {
      const resizeEvent = new Event('resize');
      window.dispatchEvent(resizeEvent);
      clearResizeTimer();
    }, 100);
  };

  const handleMenuTransitionEnd: React.TransitionEventHandler<HTMLDivElement> = (e) => {
    e?.preventDefault();
    if (e?.propertyName === 'width') {
      handleMenuWidthTransitionEnd(e);
    }
  };

  return {
    onMenuTransitionEnd: handleMenuTransitionEnd,
  };
}

export default (props: ILeftMenuPropsType) => {
  const {
    title: titleEN,
    titleCN,
    menuList,
    children,
    clickMenu,
    customFooter,
    customFooterShrink,
    customHeaderElement,
    isHyperlink,
    bottomSlot,
    currentPath,
    isShowShrinkMenuTextBubble
  } = props;
  const [isNormal, setNormal] = useState(true);
  const [containerWidth, setContainerWidth] = useState<number>(DEFAULT_WIDTH);
  const [menuWidth, setMenuWidth] = useState<number>(DEFAULT_WIDTH);
  const splitterRef = useRef<HTMLDivElement | null>(null);
  const dispatch = useDispatch();
  useSyncCopilotLeftMenuCollapse(setNormal);
  // const { locale } = useIntl();
  const locale = getLocale();
  const isEN = locale === LANGUAGE_EN;
  const title = isEN ? titleEN : titleCN || titleEN;

  const { onMenuTransitionEnd } = useMenuTransitionEnd();

  // 处理拖动
  useEffect(() => {
    const $div = splitterRef.current;
    if ($div) {
      // 鼠标移动
      let dragFlag = false;
      let tmpWidth = 0;
      const handleMouseMove = (e: MouseEvent) => {
        if (!dragFlag) {
          return;
        }
        tmpWidth = e.clientX;
        if (tmpWidth < DEFAULT_WIDTH) {
          tmpWidth = DEFAULT_WIDTH;
        } else if (tmpWidth > MAX_WIDTH) {
          tmpWidth = MAX_WIDTH;
        }
        setMenuWidth(tmpWidth);
      };

      // 鼠标松开
      const handleMouseUp = () => {
        if (!dragFlag) {
          return;
        }
        // 防止仅点击鼠标没有实际拖拽菜单时就松开鼠标，导致菜单宽度为0
        if (!tmpWidth) return;
        dragFlag = false;
        dispatch({
          type: 'home/save',
          payload: {
            showGlobalMask: false,
          },
        });
        setContainerWidth(tmpWidth);
      };

      // 鼠标按下
      const handleMouseDown = () => {
        dragFlag = true;
        dispatch({
          type: 'home/save',
          payload: {
            showGlobalMask: true,
          },
        });
      };
      $div.addEventListener('mousedown', handleMouseDown);
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        $div.removeEventListener('mousedown', handleMouseDown);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, []);

  // // 触发window resize
  // useEffect(() => {
  //   const resizeEvent = new Event('resize');
  //   window.dispatchEvent(resizeEvent);
  // }, [containerWidth, isNormal]);

  const onSet = (value: any) => {
    setNormal(!value);
  };

  return (
    <>
      <div
        className={`db-left-menu-container ${isNormal ? 'normal-with' : 'small-with'} ${
          !bottomSlot ? 'no-ft' : ''
        }`}
        style={isNormal ? { width: `${containerWidth}px` } : {}}
        onTransitionEnd={onMenuTransitionEnd}
      >
        <div
          className={`navmenu ${isNormal ? 'normal-with' : 'small-with'}`}
          style={isNormal ? { width: `${menuWidth}px` } : {}}
        >
          {/* 头部title */}
          {customHeaderElement ? (
            React.Children.map(customHeaderElement, (child) => {
              return React.cloneElement(child, {
                isNormal,
                onSet,
                menuWidth,
                title,
              });
            })
          ) : (
            <MenuHeader isNormal={isNormal} setNormal={setNormal}>
              {title}
            </MenuHeader>
          )}

          {/* 菜单列表,支持自定义菜单 */}
          {children ? (
            React.Children.map(children, (child) => {
              return React.cloneElement(child, {
                isNormal,
                onSet,
                menuWidth,
              });
            })
          ) : (
            <MenuListWrap
              isNormal={isNormal}
              setNormal={setNormal}
              menuList={menuList}
              clickMenu={clickMenu}
              isHyperlink={isHyperlink}
              currentPath={currentPath}
              isShowShrinkMenuTextBubble={isShowShrinkMenuTextBubble}
            ></MenuListWrap>
          )}

          {customFooter && (
            <div className="menu-footer">{isNormal ? customFooter : customFooterShrink}</div>
          )}

          {/* 展开收起按钮 */}
          <div className="navmenu-ft">
            <div className="navmenu-ft-left">{bottomSlot}</div>
            <div
              className="navmenu-ft-right"
              onClick={() => {
                setNormal(!isNormal);
              }}
            >
              <FoldButton isExpand={isNormal} />
            </div>
          </div>
          <div className="db-common-left-menu-splitter" ref={splitterRef}></div>
        </div>
      </div>
      <GlobalMask />
    </>
  );
};
